import { getPageByName } from "@lg-cfa/utils";
import { navigate } from "gatsby";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { FlowTypes, PageNames } from "@enums";
import { useClearData, usePageDataValidator } from "@hooks";
import { currentPageState, pagesState } from "@states";
import { historyState } from "@states/history";
import {
    weTakeCareActivated,
    weTakeCareReason,
    weTakeCareStepout,
} from "@states/userData";
import { PageHistory } from "@types";

export function useFlowNavigation() {
    const page = useRecoilValue(currentPageState);
    const setWeTakeCareActivated = useSetRecoilState(weTakeCareActivated);
    const setWeTakeCareStepout = useSetRecoilState(weTakeCareStepout);
    const setWeTakeCareReason = useSetRecoilState(weTakeCareReason);
    const [history, setHistory] = useRecoilState(historyState);
    const allPages = useRecoilValue(pagesState);
    const { clearAll } = useClearData();
    const { isValidPage } = usePageDataValidator();

    /**
     * Resets the flow to the initial state.
     *
     * This function clears the navigation history, performs any necessary cleanup,
     * and navigates to the welcome page. It accepts an optional parameter to specify
     * the type of flow to reset to, defaulting to `FlowTypes.Generic`.
     *
     * @param {`${FlowTypes}`} [pageFlow=FlowTypes.Generic] - The type of flow to reset to.
     */
    const resetFlow = (pageFlow: `${FlowTypes}` = FlowTypes.Generic) => {
        clearAll();
        navigateTo(PageNames.Welcome, true, pageFlow);
    };

    /**
     * Navigates to a specified page if it is valid.
     *
     * @param pageName - The name of the page to navigate to.
     * @param isValidated - Optional flag indicating if the page has already been validated. Defaults to false.
     * @param pageFlow - Optional string representing the flow of the page.
     *
     * @returns void
     */
    const navigateTo = (
        pageName: string,
        isValidated = false,
        pageFlow?: string,
    ) => {
        const nextPage = getPageByName(allPages, pageName, pageFlow);
        if (!nextPage) return;

        const pageIsValid = isValidated || isValidPage(nextPage);

        if (pageIsValid) {
            navigate(nextPage.url);
        }
    };

    /**
     * Navigates to the next page in the flow.
     *
     * @param {string} [pageFlow=FlowTypes.Generic] - The flow type to use for determining the next page. Defaults to `FlowTypes.Generic`.
     * @returns {void} - This function does not return a value.
     *
     * The function attempts to find the next page in the specified flow. If the next page is found and is valid, it navigates to the URL of the next page.
     * If the next page is not found or is not valid, the function does nothing.
     */
    const next = (pageFlow: string = FlowTypes.Generic) => {
        const nextPage =
            getPageByName(allPages, page?.nextPage, pageFlow) ??
            getPageByName(allPages, page?.nextPage, FlowTypes.Generic);
        if (!nextPage) return;

        if (isValidPage(nextPage)) {
            navigate(nextPage.url);
        }
    };

    /**
     * Navigates to the previous page in the flow navigation history.
     *
     * @returns {void} This function does not return a value.
     */
    const prev = () => {
        if (history.length <= 0) {
            return;
        }

        // Remove the last element from the history array since this is the current page
        const historyArray = history.slice(0, -1);
        const buildedHistory = buildHistory();
        // Get the last element from the history array
        // If the history array is empty, get the last element from the builded history array
        const lastHistoryEntry = historyArray[historyArray.length - 1];
        const prevPageHistory =
            lastHistoryEntry ?? buildedHistory[buildedHistory.length - 1];

        if (!prevPageHistory) {
            return;
        }

        // Get the previous page based on the name and flow from the prevPageHistory
        const prevPage = getPageByName(
            allPages,
            prevPageHistory.name,
            prevPageHistory.flow,
        );

        if (!prevPage) {
            return;
        }

        if (
            prevPage.name !== PageNames.BrokerSearch &&
            prevPage.name !== PageNames.YourInformation
        ) {
            setWeTakeCareActivated(false);
            setWeTakeCareStepout(false);
            setWeTakeCareReason({
                reason: undefined,
                page: undefined,
                businessRule: undefined,
            });
        }

        /**
         *   Remove the last element of the history array since this is the page
         *   where we are navigating back to
         *   If we don't remove the last element, we would end up with double entries
         *   because the page we are navigating back to is already in the history array
         *   and will be added again after navigating back.
         */
        const newHistoryArray = lastHistoryEntry
            ? historyArray.slice(0, -1)
            : buildedHistory.slice(0, -1);

        setHistory(newHistoryArray);

        navigate(prevPage.url);
    };

    /**
     * Builds the history of pages navigated through in a specific flow.
     *
     * @returns {PageHistory[]} An array of `PageHistory` objects representing the navigation history.
     *
     * @remarks
     * This function iterates through the pages starting from the current page and traces back through the `prevPage` property
     * until there are no more previous pages. It constructs an array of `PageHistory` objects, each containing the name and flow
     * of the page, in reverse order (from the current page to the first page in the history).
     *
     * @example
     * ```typescript
     * const history = buildHistory(FlowTypes.SpecificFlow);
     * console.log(history);
     * // Output: [
     * //   { name: 'Page1', flow: FlowTypes.SpecificFlow },
     * //   { name: 'Page2', flow: FlowTypes.SpecificFlow },
     * //   ...
     * // ]
     * ```
     */
    const buildHistory = (): PageHistory[] => {
        const historyArray: PageHistory[] = [];
        let pageToCheck = page;
        let continueIteration = true;
        while (continueIteration) {
            if (pageToCheck?.prevPage && pageToCheck.prevPage !== undefined) {
                pageToCheck =
                    getPageByName(
                        allPages,
                        pageToCheck.prevPage,
                        pageToCheck.flow,
                    ) ??
                    getPageByName(
                        allPages,
                        pageToCheck.prevPage,
                        FlowTypes.Generic,
                    );

                if (pageToCheck) {
                    historyArray.unshift({
                        name: pageToCheck.name as PageNames,
                        flow: pageToCheck.flow as FlowTypes,
                    });
                }
            } else {
                continueIteration = false;
            }
        }
        return historyArray;
    };

    return { next, prev, navigateTo, resetFlow };
}
